import { z } from "zod";
import type { PageContextEnvelope } from "@tabbio/types";

/**
 * Validation for the client-reported page-context envelope that rides along
 * with agent chat requests (`AgentChatRequest.pageContext`).
 *
 * This is CLIENT-SUPPLIED data that ends up inside the agent's system
 * instructions, so the schema is deliberately strict: every object is
 * field-whitelisted (unknown keys reject), every string is hard-capped, and
 * the free-form `state` record is size-capped after serialization. The API
 * drops (and logs) anything that fails here instead of erroring the request.
 */

export const PAGE_CONTEXT_ROUTE_MAX = 256;
export const PAGE_CONTEXT_ENTITY_ID_MAX = 128;
export const PAGE_CONTEXT_ENTITY_LABEL_MAX = 200;
/** Cap on `JSON.stringify(state).length` — 2KB of serialized screen state. */
export const PAGE_CONTEXT_STATE_MAX_CHARS = 2048;

export const PAGE_CONTEXT_SCREENS = [
  "chat",
  "cv",
  "cv-tailored",
  "jobs",
  "job-detail",
  "skills",
  "skill-detail",
  "automation",
  "connectors",
  "memory",
  "analytics",
  "profile",
  "messages",
  "notifications",
  "application-studio",
  "settings",
  "applications",
  "application-detail",
  "company-page",
  "other",
] as const;

export const PAGE_CONTEXT_ENTITY_TYPES = [
  "cv",
  "tailoredCv",
  "catalogJob",
  "job",
  "skill",
  "automation",
  "connector",
  "conversation",
  "notification",
  "studioProject",
  "studioArtifact",
  "application",
  "talentSubmission",
] as const;

export const pageContextScreenSchema = z.enum(PAGE_CONTEXT_SCREENS);

export const pageContextEntityTypeSchema = z.enum(PAGE_CONTEXT_ENTITY_TYPES);

export const pageContextEntitySchema = z
  .object({
    type: pageContextEntityTypeSchema,
    id: z.string().min(1).max(PAGE_CONTEXT_ENTITY_ID_MAX),
    label: z.string().max(PAGE_CONTEXT_ENTITY_LABEL_MAX).optional(),
  })
  .strict();

const pageContextStateSchema = z
  .record(z.unknown())
  .superRefine((state, ctx) => {
    let serialized: string;
    try {
      serialized = JSON.stringify(state) ?? "";
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "state must be JSON-serializable",
      });
      return;
    }
    if (serialized.length > PAGE_CONTEXT_STATE_MAX_CHARS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `state exceeds ${PAGE_CONTEXT_STATE_MAX_CHARS} serialized characters`,
      });
    }
  });

export const pageContextEnvelopeSchema = z
  .object({
    v: z.literal(1),
    screen: pageContextScreenSchema,
    route: z.string().min(1).max(PAGE_CONTEXT_ROUTE_MAX),
    entity: pageContextEntitySchema.optional(),
    state: pageContextStateSchema.optional(),
    capturedAt: z.string().datetime({ offset: true }),
  })
  .strict();

// Compile-time guarantee that the schema output stays assignable to the shared
// envelope type: drifting the schema away from @tabbio/types breaks this line.
type SchemaOutput = z.infer<typeof pageContextEnvelopeSchema>;
const _assertAssignable: PageContextEnvelope = {} as SchemaOutput;
void _assertAssignable;

/**
 * Parses an unknown value into a validated envelope, or `null` when it is
 * absent or invalid. Never throws: a malformed envelope must degrade to
 * "no page context", not fail the chat request.
 */
export function parsePageContextEnvelope(
  value: unknown,
): PageContextEnvelope | null {
  if (value == null) return null;
  const parsed = pageContextEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
